import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FocusModeProvider } from "./context/FocusModeContext";
import { ExperienceModeProvider } from "./context/ExperienceModeContext";
import { TenantProvider } from "./context/TenantContext";

// Pages
import HomePage from "./pages/HomePage";
import PricingPage from "./pages/PricingPage";
import SecurityPage from "./pages/SecurityPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardPage from "./pages/DashboardPage";
import SignatureWelcomePage from "./pages/SignatureWelcomePage";
import LaunchModePage from "./pages/LaunchModePage";
import MyPlanPage from "./pages/MyPlanPage";
import WaitlistPage from "./pages/WaitlistPage";
import InsightsPage from "./pages/InsightsPage";
import ArticlePage from "./pages/ArticlePage";
import AuthorPage from "./pages/AuthorPage";
import AdminContentPage from "./pages/AdminContentPage";
import AiEconomicsPage from "./pages/AiEconomicsPage";
import AdminOrganizationsPage from "./pages/AdminOrganizationsPage";
import AdminConsolePage from "./pages/AdminConsolePage";
import OrgConsolePage from "./pages/OrgConsolePage";
import OrgActivatePage from "./pages/OrgActivatePage";
import ClaimProfilePage from "./pages/ClaimProfilePage";
import SetOwnPasswordPage from "./pages/SetOwnPasswordPage";
import OrgPortalPage from "./pages/OrgPortalPage";
import TenantAuthPage from "./pages/TenantAuthPage";
import LabsJoinPage from "./pages/LabsJoinPage";
import LabsAdminPage from "./pages/LabsAdminPage";
import LabsPreviewLanding from "./pages/LabsPreviewLanding";
import PathwayDashboard from "./pages/PathwayDashboard";
import DecisionLab from "./pages/DecisionLab";
import VaultPage from "./pages/VaultPage";
import PlanPage from "./pages/PlanPage";
import CareersPage from "./pages/CareersPage";
import PathwayIntelligencePage from "./pages/PathwayIntelligencePage";
import MajorIntelligencePage from "./pages/MajorIntelligencePage";
import ComparePathwaysPage from "./pages/ComparePathwaysPage";
import CounselorCaseloadPage from "./pages/CounselorCaseloadPage";
import SuperintendentDashboardPage from "./pages/SuperintendentDashboardPage";
import BondIntelligencePage from "./pages/BondIntelligencePage";
import BillingPage from "./pages/BillingPage";
import ProfilePage from "./pages/ProfilePage";
import ProfileRouter from "./pages/ProfileRouter";
import ReportPage from "./pages/ReportPage";
import LearnPage from "./pages/LearnPage";
import LifestyleDesignPage from "./pages/LifestyleDesignPage";
import CreditGate from "./components/CreditGate";
import FinancialLiteracyPage from "./pages/FinancialLiteracyPage";
import FutureFinancialOutlookPage from "./pages/FutureFinancialOutlookPage";
import ScholarshipsPage from "./pages/ScholarshipsPage";
import ExperiencesPage from "./pages/ExperiencesPage";
import JobsPage from "./pages/JobsPage";
import ProfileIntelligencePage from "./pages/ProfileIntelligencePage";
import ParentDashboardPage from "./pages/ParentDashboardPage";
import ParentAcceptInvitePage from "./pages/ParentAcceptInvitePage";
import GoalsPage from "./pages/GoalsPage";
import MilitaryPathPage from "./pages/MilitaryPathPage";
import SkilledTradesPage from "./pages/SkilledTradesPage";
import DirectWorkforcePage from "./pages/DirectWorkforcePage";
import FutureCareersPage from "./pages/FutureCareersPage";
import ExamDatesPage from "./pages/ExamDatesPage";
import ProgramsPage from "./pages/ProgramsPage";
import EntrepreneurshipPage from "./pages/EntrepreneurshipPage";
import ResumeATSPage from "./pages/ResumeATSPage";
import SkillsPage from "./pages/SkillsPage";
import CollegeIntelPage from "./pages/CollegeIntelPage";

import ApplicationCommandCenterPage from "./pages/ApplicationCommandCenterPage";

import AcademicPlanPage from "./pages/AcademicPlanPage";
import DiscoverMePage from "./pages/DiscoverMePage";

import ChoosePathPage from "./pages/ChoosePathPage";
import ShowcasePage from "./pages/ShowcasePage";
import DemoProfileSwitcher from "./components/DemoProfileSwitcher";
import IntelligenceHubPage from "./pages/IntelligenceHubPage";
import ReadinessPage from "./pages/ReadinessPage";
import PositionMePage from "./pages/PositionMePage";
import ConnectMePage from "./pages/ConnectMePage";
import OpportunityRadarPage from "./pages/OpportunityRadarPage";
import FutureSelfPage from "./pages/FutureSelfPage";
import LearningRoiPage from "./pages/LearningRoiPage";
import AssessmentPage from "./pages/AssessmentPage";
import TransitionIntelligencePage from "./pages/TransitionIntelligencePage";
import FutureOfWorkPage from "./pages/FutureOfWorkPage";
import FundingPathPage from "./pages/FundingPathPage";
import ParentDashboard from "./pages/ParentDashboard";
import JoinFamilyPage from "./pages/JoinFamilyPage";
import ParentFinancialToolkit from "./pages/ParentFinancialToolkit";
import ParentFinancialIntelligence from "./pages/ParentFinancialIntelligence";
import EnterpriseDashboard from "./pages/EnterpriseDashboard";
import IntelligencePage from "./pages/IntelligencePage";
import EmployerDashboard from "./pages/EmployerDashboard";
import PrivacySettings from "./pages/PrivacySettings";
import ApplicationPipeline from "./pages/ApplicationPipeline";
import DecisionWorkflow from "./pages/DecisionWorkflow";
import FamilyDecisionWorkflow from "./pages/FamilyDecisionWorkflow";
import AdvancedFinancial from "./pages/AdvancedFinancial";
import CollegeFinancialPage from "./pages/CollegeFinancialPage";
import CareerLandscapePage from "./pages/CareerLandscapePage";
import ScholarshipsGrantsPage from "./pages/ScholarshipsGrantsPage";
import RealityPlanPage from "./pages/RealityPlanPage";
import NotificationsPage from "./pages/NotificationsPage";
import StartHerePage from "./pages/StartHerePage";
import CounselorMeetingsPage from "./pages/CounselorMeetingsPage";
import SecurityCenterPage from "./pages/SecurityCenterPage";
import NotificationSettings from "./pages/NotificationSettings";
import MyPathsPage from "./pages/MyPathsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import TrustCompliancePage from "./pages/TrustCompliancePage";
import AdminConsentAuditPage from "./pages/AdminConsentAuditPage";
import AdminPrivacyCompliancePage from "./pages/AdminPrivacyCompliancePage";
import AdminExamDatesPage from "./pages/AdminExamDatesPage";
import AdminDeploymentStatusPage from "./pages/AdminDeploymentStatusPage";
import AdminQAConsolePage from "./pages/AdminQAConsolePage";
import AdminVersionBanner from "./components/AdminVersionBanner";
import DemoSwitcher from "./components/DemoSwitcher";
import SkillsAssessmentPage from "./pages/SkillsAssessmentPage";
import MasterAdminPage from "./pages/MasterAdminPage";
import UserManagementPage from "./pages/UserManagementPage";
import PortfolioPage from "./pages/PortfolioPage";
import AccountSetupPage from "./pages/AccountSetupPage";
import ParentRegistrationFlow from "./pages/ParentRegistrationFlow";
import AdultStudentRegistration from "./pages/AdultStudentRegistration";
import StudentActivationPage from "./pages/StudentActivationPage";
import TrustPage from "./pages/TrustPage";
import FinancialPreviewPage from "./pages/FinancialPreviewPage";
import TuitionCalculatorPage from "./pages/TuitionCalculatorPage";
import SupportAdminPage from "./pages/SupportAdminPage";
import PrivacyCenterPage from "./pages/PrivacyCenterPage";
import NCCareerPlanPage from "./pages/NCCareerPlanPage";
import NCCTEPathwaysPage from "./pages/NCCTEPathwaysPage";
import WBLTrackerPage from "./pages/WBLTrackerPage";
import CertificateProgramsPage from "./pages/CertificateProgramsPage";
import CredentialTrackerPage from "./pages/CredentialTrackerPage";
import GraduationPredictionPage from "./pages/GraduationPredictionPage";
import CoursePlannerPage from "./pages/CoursePlannerPage";
import AcademicAlignmentPage from "./pages/AcademicAlignmentPage";
import EventSchedulerPage from "./pages/EventSchedulerPage";
import RecommendationLettersPage from "./pages/RecommendationLettersPage";
import WakeCountyDemoPage from "./pages/WakeCountyDemoPage";
import InstitutionalStudentDashboard from "./pages/InstitutionalStudentDashboard";
import FuturePathCDPPage from "./pages/FuturePathCDPPage";
import MySchoolPlanPage from "./pages/MySchoolPlanPage";
import ParentCDPViewPage from "./pages/ParentCDPViewPage";
import CollegeDashboardPage from "./pages/CollegeDashboardPage";
import CareerIntelligencePage from "./pages/CareerIntelligencePage";
import PathSimulatorPage from "./pages/PathSimulatorPage";
import ProofBuilderPage from "./pages/ProofBuilderPage";
import MyStrategyPage from "./pages/MyStrategyPage";
import MySituationPage from "./pages/MySituationPage";
import HSDashboardPage from "./pages/HSDashboardPage";

// Middle School Pages
import MiddleSchoolHome from "./pages/MiddleSchoolHome";
import MiddleSchoolLayout from "./components/MiddleSchoolLayout";
import {
  MSAboutMePage,
  MSQuizPage,
  MSStrengthsPage,
  MSCareersPage,
  MSDreamLifePage,
  MSDreamsPage,
  MSFutureJobsPage,
  MSMissionsPage,
  MSProgramsPage,
  MSLearnPage,
  MSBadgesPage,
  MSProfilePage
} from "./pages/ms";

// Context Providers
import { LearningStageProvider } from "./context/LearningStageContext";
import { ActiveChildProvider } from "./context/ActiveChildContext";
import { TenantBrandProvider } from "./context/TenantBrandContext";
import SubjectExitPill from "./components/SubjectExitPill";
import { PathScopeProvider } from "./context/PathScopeContext";
import ParentOnly from "./components/ParentOnly";
import LearnHubPage from "./pages/LearnHubPage";
import { CopyProvider } from "./context/CopyContext";
import { FuturePathProvider } from "./context/FuturePathContext";
import ParentSelfReflectionGate from "./components/ParentSelfReflectionGate";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // If user data was passed from AuthCallback, use it
  if (location.state?.user) {
    return children;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-navy-gradient flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // A guardian set this account up: nothing else happens until the young person owns the password.
  if (user.must_change_password && location.pathname !== '/app/set-password') {
    return <Navigate to="/app/set-password" replace />;
  }

  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-navy-gradient flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }
  
  if (user && user.must_change_password) {
    return <Navigate to="/app/set-password" replace />;
  }

  if (user && user.onboarding_complete) {
    // Redirect based on user role
    const enterpriseRoles = ['super_admin', 'org_admin', 'counselor', 'university_career_staff', 'workforce_manager'];
    const institutionalRoles = ['district_admin', 'superintendent', 'principal'];
    const employerRoles = ['employer_admin', 'employer_recruiter'];

    // Organization staff aren't participants — they belong in their own console.
    if (user.role === 'org_staff') {
      return <Navigate to="/app/org" replace />;
    }

    // Institutional roles (superintendent, district admin, principal) → district overview
    if (institutionalRoles.includes(user.role)) {
      return <Navigate to="/app/institution/superintendent" replace />;
    }

    // Counselors get the Command Center directly
    if (user.role === 'counselor') {
      return <Navigate to="/app/pathways/counselor" replace />;
    }
    
    if (enterpriseRoles.includes(user.role)) {
      return <Navigate to="/enterprise" replace />;
    } else if (employerRoles.includes(user.role)) {
      return <Navigate to="/app/employer" replace />;
    } else if (user.role === 'parent') {
      return <Navigate to="/app/parent/child-cdp" replace />;
    } else if (user.role === 'facilitator') {
      return <Navigate to="/app/admin?tab=labs" replace />;
    }
    
    // Redirect students based on learning stage
    if (user.learning_stage === 'middle_school' || (user.grade && user.grade >= 6 && user.grade <= 8)) {
      return <Navigate to="/ms/home" replace />;
    }
    
    // High school students go to the main dashboard
    return <Navigate to="/app/dashboard" replace />;
  }
  
  if (user && !user.onboarding_complete) {
    // Skip onboarding for enterprise/employer/institutional users AND for parents
    // whose student is in a school-linked (district) account — the district roster
    // already provides the link, so parents don't need a separate onboarding.
    const skipOnboardingRoles = ['super_admin', 'org_admin', 'counselor', 'employer_admin', 'employer_recruiter',
      'university_career_staff', 'workforce_manager', 'district_admin', 'superintendent', 'principal',
      'parent'];
    if (skipOnboardingRoles.includes(user.role)) {
      if (['district_admin', 'superintendent', 'principal'].includes(user.role)) {
        return <Navigate to="/app/institution/superintendent" replace />;
      }
      if (user.role === 'counselor') {
        return <Navigate to="/app/pathways/counselor" replace />;
      }
      if (user.role === 'parent') {
        return <Navigate to="/app/parent/child-cdp" replace />;
      }
      if (['super_admin', 'org_admin', 'university_career_staff', 'workforce_manager'].includes(user.role)) {
        return <Navigate to="/enterprise" replace />;
      } else {
        return <Navigate to="/app/employer" replace />;
      }
    }
    // Organization staff aren't participants — they belong in their own console.
    if (user.role === 'org_staff') {
      return <Navigate to="/app/org" replace />;
    }
    // High school students (education_level='high_school' OR learning_stage='high_school'
    // OR any student rostered by a school/organization) skip the general welcome flow —
    // their district already provides the profile context. Fall through to render the
    // requested protected page directly.
    if (user.role === 'student' && (
      user.education_level === 'high_school' ||
      user.learning_stage === 'high_school' ||
      user.organization_id ||
      user.school_id ||
      user.current_sponsor_org_id
    )) {
      return children;
    }
    // Parents run the same integrated welcome as everyone else — their own basics and
    // archetype first, then adding children with consent — before the family dashboard.
    return <Navigate to="/app/welcome" replace />;
  }
  
  return children;
};

// Router component to check for session_id in URL
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  // This MUST run synchronously during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Marketing Pages */}
      <Route path="/" element={<PublicRoute><HomePage /></PublicRoute>} />
      <Route path="/waitlist" element={<WaitlistPage />} />
      <Route path="/insights" element={<InsightsPage />} />
      <Route path="/insights/category/:categorySlug" element={<InsightsPage />} />
      <Route path="/insights/author/:authorSlug" element={<AuthorPage />} />
      <Route path="/insights/:slug" element={<ArticlePage />} />
      <Route path="/blog" element={<InsightsPage />} />
      <Route path="/blog/category/:categorySlug" element={<InsightsPage />} />
      <Route path="/blog/author/:authorSlug" element={<AuthorPage />} />
      <Route path="/blog/:slug" element={<ArticlePage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/security" element={<SecurityPage />} />
      
      {/* Auth Pages */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<Navigate to="/pricing" replace />} />
      {/* The role-picker page is retired: everyone chooses a plan first. */}
      <Route path="/register" element={<Navigate to="/pricing" replace />} />
      <Route path="/AFPLabs" element={<LabsJoinPage />} />
      <Route path="/AFPLabs/preview" element={<LabsPreviewLanding />} />
      <Route path="/AFPLabs/join/:code" element={<LabsJoinPage />} />
      <Route path="/AFPLabs/showcase" element={<ShowcasePage />} />
      <Route path="/demo" element={<ShowcasePage />} />
      <Route path="/labs" element={<LabsJoinPage />} />
      <Route path="/labs/join/:code" element={<LabsJoinPage />} />
      <Route path="/register/parent" element={<ParentRegistrationFlow />} />
      <Route path="/register/student" element={<AdultStudentRegistration />} />
      <Route path="/register/setup" element={<AccountSetupPage />} />
      <Route path="/activate/:token" element={<StudentActivationPage />} />
      <Route path="/trust" element={<TrustPage />} />
      <Route path="/financial-preview" element={<FinancialPreviewPage />} />
      <Route path="/tuition-calculator" element={<TuitionCalculatorPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      
      {/* Onboarding */}
      {/* Retired: the profile builder now lives inside the Signature Welcome. */}
      <Route path="/onboarding" element={<Navigate to="/app/welcome" replace />} />
      
      {/* Protected App Pages */}
      <Route path="/app/pathways" element={
        <ProtectedRoute>
          <PathwayIntelligencePage />
        </ProtectedRoute>
      } />
      <Route path="/app/pathways/compare" element={
        <ProtectedRoute>
          <ComparePathwaysPage />
        </ProtectedRoute>
      } />
      <Route path="/app/pathways/counselor" element={
        <ProtectedRoute>
          <CounselorCaseloadPage />
        </ProtectedRoute>
      } />
      <Route path="/app/institution/superintendent" element={
        <ProtectedRoute>
          <SuperintendentDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/app/institution/bond-intelligence" element={
        <ProtectedRoute>
          <BondIntelligencePage />
        </ProtectedRoute>
      } />
      <Route path="/app/pathways/majors/:cipCode" element={
        <ProtectedRoute>
          <MajorIntelligencePage />
        </ProtectedRoute>
      } />
      <Route path="/app/dashboard" element={
        <ProtectedRoute>
          <HSDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/app/dashboard-legacy" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/app/college-dashboard" element={
        <ProtectedRoute>
          <CollegeDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/app/career-intelligence" element={
        <ProtectedRoute>
          <CareerIntelligencePage />
        </ProtectedRoute>
      } />
      <Route path="/app/career-landscape" element={
        <ProtectedRoute>
          <CareerLandscapePage />
        </ProtectedRoute>
      } />
      <Route path="/app/path-simulator" element={
        <ProtectedRoute>
          <PathSimulatorPage />
        </ProtectedRoute>
      } />
      <Route path="/app/proof-builder" element={
        <ProtectedRoute>
          <ProofBuilderPage />
        </ProtectedRoute>
      } />
      <Route path="/app/my-strategy" element={
        <ProtectedRoute>
          <MyStrategyPage />
        </ProtectedRoute>
      } />
      <Route path="/app/my-situation" element={
        <ProtectedRoute>
          <MySituationPage />
        </ProtectedRoute>
      } />
      <Route path="/app/welcome" element={
        <ProtectedRoute>
          <SignatureWelcomePage />
        </ProtectedRoute>
      } />
      <Route path="/app/launch" element={
        <ProtectedRoute>
          <LaunchModePage />
        </ProtectedRoute>
      } />
      <Route path="/app/admin/content" element={
        <ProtectedRoute>
          <AdminContentPage />
        </ProtectedRoute>
      } />
      {/* One administrative roof: tenants, labs, economics and notifications are tabs, and the
          old addresses land on the right tab instead of separate pages. */}
      <Route path="/app/admin" element={
        <ProtectedRoute>
          <AdminConsolePage />
        </ProtectedRoute>
      } />
      <Route path="/app/admin/ai-economics" element={<Navigate to="/app/admin?tab=economics" replace />} />
      <Route path="/app/admin/organizations" element={<Navigate to="/app/admin?tab=tenants" replace />} />
      <Route path="/app/admin/trust-compliance" element={
        <ProtectedRoute>
          <TrustCompliancePage />
        </ProtectedRoute>
      } />
      <Route path="/app/org" element={
        <ProtectedRoute>
          <OrgConsolePage />
        </ProtectedRoute>
      } />
      <Route path="/app/my-plan" element={
        <ProtectedRoute>
          <MyPlanPage />
        </ProtectedRoute>
      } />
      <Route path="/app/labs" element={<Navigate to="/app/admin?tab=labs" replace />} />
      <Route path="/labs-admin" element={
        <ProtectedRoute>
          <LabsAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/app/pathway" element={
        <ProtectedRoute>
          <PathwayDashboard />
        </ProtectedRoute>
      } />
      <Route path="/app/decision-lab" element={
        <ProtectedRoute>
          <DecisionLab />
        </ProtectedRoute>
      } />
      <Route path="/app/vault" element={
        <ProtectedRoute>
          <VaultPage />
        </ProtectedRoute>
      } />
      <Route path="/app/plan" element={<Navigate to="/app/my-plan" replace />} />
      <Route path="/app/careers" element={
        <ProtectedRoute>
          <CareersPage />
        </ProtectedRoute>
      } />
      {/* College Explorer merged into College Intelligence (save + compare live there now) */}
      <Route path="/app/colleges" element={<Navigate to="/app/college-intel" replace />} />
      <Route path="/app/billing" element={
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      } />
      <Route path="/app/profile" element={
        <ProtectedRoute>
          <ProfileRouter />
        </ProtectedRoute>
      } />
      {/* The old personality quiz was superseded by the scored 40-item assessment. */}
      <Route path="/app/quiz" element={<Navigate to="/app/assessment" replace />} />
      <Route path="/app/report" element={
        <ProtectedRoute>
          <ReportPage />
        </ProtectedRoute>
      } />
      <Route path="/app/learn" element={
        <ProtectedRoute>
          <LearnHubPage />
        </ProtectedRoute>
      } />
      <Route path="/app/college-intel" element={
        <ProtectedRoute>
          <CollegeIntelPage />
        </ProtectedRoute>
      } />
      <Route path="/app/college-explorer" element={<Navigate to="/app/college-intel" replace />} />
      {/* College Application Command Center — pipeline + detail on same page */}
      <Route path="/app/applications" element={
        <ProtectedRoute>
          <ApplicationCommandCenterPage />
        </ProtectedRoute>
      } />
      <Route path="/app/applications/:appId" element={
        <ProtectedRoute>
          <ApplicationCommandCenterPage />
        </ProtectedRoute>
      } />
      <Route path="/app/opportunities" element={<Navigate to="/app/internships" replace />} />
      {/* Academic Plan of Study — multi-year pathway courses */}
      <Route path="/app/academic-plan" element={
        <ProtectedRoute>
          <AcademicPlanPage />
        </ProtectedRoute>
      } />
      {/* Discover Me 2.0 — 7-dimension structured assessment */}
      <Route path="/app/discover-me" element={
        <ProtectedRoute>
          <DiscoverMePage />
        </ProtectedRoute>
      } />
      <Route path="/app/careers/deep" element={<Navigate to="/app/careers" replace />} />
      <Route path="/app/careers/deep/:soc" element={<Navigate to="/app/careers" replace />} />
      <Route path="/app/intelligence" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/app/transition" element={
        <ProtectedRoute>
          <TransitionIntelligencePage />
        </ProtectedRoute>
      } />
      <Route path="/app/future-of-work" element={
        <ProtectedRoute>
          <FutureOfWorkPage />
        </ProtectedRoute>
      } />
      <Route path="/app/funding" element={
        <ProtectedRoute>
          <FundingPathPage />
        </ProtectedRoute>
      } />
      <Route path="/app/readiness" element={
        <ProtectedRoute>
          <ReadinessPage />
        </ProtectedRoute>
      } />
      <Route path="/app/position" element={
        <ProtectedRoute>
          <PositionMePage />
        </ProtectedRoute>
      } />
      <Route path="/app/connect" element={
        <ProtectedRoute>
          <ConnectMePage />
        </ProtectedRoute>
      } />
      <Route path="/app/radar" element={
        <ProtectedRoute>
          <OpportunityRadarPage />
        </ProtectedRoute>
      } />
      <Route path="/app/future-self" element={
        <ProtectedRoute>
          <FutureSelfPage />
        </ProtectedRoute>
      } />
      <Route path="/app/learning-roi" element={
        <ProtectedRoute>
          <LearningRoiPage />
        </ProtectedRoute>
      } />
      <Route path="/app/ai-credits" element={<Navigate to="/app/billing" replace />} />
      <Route path="/app/assessment" element={
        <ProtectedRoute>
          <AssessmentPage />
        </ProtectedRoute>
      } />
      <Route path="/app/choose-path" element={
        <ProtectedRoute>
          <ChoosePathPage />
        </ProtectedRoute>
      } />
      <Route path="/app/skills" element={
        <ProtectedRoute>
          <SkillsPage />
        </ProtectedRoute>
      } />
      <Route path="/app/resume-ats" element={
        <ProtectedRoute>
          <ResumeATSPage />
        </ProtectedRoute>
      } />
      <Route path="/app/family" element={
        <ProtectedRoute>
          <ParentOnly>
            <ParentDashboard />
          </ParentOnly>
        </ProtectedRoute>
      } />
      <Route path="/app/family/decisions" element={
        <ProtectedRoute>
          <ParentOnly>
            <FamilyDecisionWorkflow />
          </ParentOnly>
        </ProtectedRoute>
      } />
      <Route path="/join-family/:token" element={<JoinFamilyPage />} />
      <Route path="/app/parent/financial" element={
        <ProtectedRoute>
          <ParentFinancialToolkit />
        </ProtectedRoute>
      } />
      <Route path="/app/parent/intelligence" element={
        <ProtectedRoute>
          <ParentFinancialIntelligence />
        </ProtectedRoute>
      } />
      <Route path="/app/scholarships-grants" element={
        <Navigate to="/app/scholarships" replace />
      } />
      <Route path="/app/calculator" element={
        <ProtectedRoute>
          <ParentSelfReflectionGate kind="calculator">
            <LifestyleDesignPage />
          </ParentSelfReflectionGate>
        </ProtectedRoute>
      } />
      <Route path="/app/cost-model" element={<Navigate to="/app/calculator" replace />} />
      <Route path="/app/my-futurepath" element={
        <ProtectedRoute>
          <ReportPage />
        </ProtectedRoute>
      } />
      <Route path="/app/scholarships" element={
        <ProtectedRoute>
          <ScholarshipsPage />
        </ProtectedRoute>
      } />
      <Route path="/app/internships" element={
        <ProtectedRoute>
          <ExperiencesPage />
        </ProtectedRoute>
      } />
      <Route path="/app/jobs" element={<Navigate to="/app/careers" replace />} />
      <Route path="/app/profile-intelligence" element={
        <ProtectedRoute>
          <ProfileIntelligencePage />
        </ProtectedRoute>
      } />
      
      {/* Privacy, Consent & Application Pipeline Routes */}
      <Route path="/app/privacy" element={
        <ProtectedRoute>
          <PrivacySettings />
        </ProtectedRoute>
      } />
      <Route path="/app/privacy-center" element={
        <ProtectedRoute>
          <PrivacyCenterPage />
        </ProtectedRoute>
      } />
      <Route path="/app/notification-settings" element={
        <ProtectedRoute>
          <NotificationSettings />
        </ProtectedRoute>
      } />
      <Route path="/app/applications" element={
        <ProtectedRoute>
          <ApplicationPipeline />
        </ProtectedRoute>
      } />
      <Route path="/app/decisions" element={<Navigate to="/app/decision-lab" replace />} />
      <Route path="/app/financial" element={
        <ProtectedRoute>
          <AdvancedFinancial />
        </ProtectedRoute>
      } />
      <Route path="/app/college-financial" element={
        <ProtectedRoute>
          <CollegeFinancialPage />
        </ProtectedRoute>
      } />
      <Route path="/app/financial-literacy" element={<Navigate to="/app/learn?tab=money-basics" replace />} />
      <Route path="/app/future-outlook" element={
        <ProtectedRoute>
          <FutureFinancialOutlookPage />
        </ProtectedRoute>
      } />
      
      {/* Scenario Lab (was "Reality Simulator" — one name, one page) */}
      <Route path="/app/scenario-lab" element={
        <ProtectedRoute>
          <RealityPlanPage />
        </ProtectedRoute>
      } />
      <Route path="/app/reality-plan" element={<Navigate to="/app/scenario-lab" replace />} />
      <Route path="/app/my-paths" element={<Navigate to="/app/decision-lab" replace />} />
      <Route path="/app/start-here" element={
        <ProtectedRoute>
          <StartHerePage />
        </ProtectedRoute>
      } />
      <Route path="/app/counselor-meetings" element={
        <ProtectedRoute>
          <CounselorMeetingsPage />
        </ProtectedRoute>
      } />
      <Route path="/app/notifications" element={
        <ProtectedRoute>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      <Route path="/app/security" element={
        <ProtectedRoute>
          <SecurityCenterPage />
        </ProtectedRoute>
      } />
      
      {/* Portfolio Route */}
      <Route path="/app/portfolio" element={
        <ProtectedRoute>
          <PortfolioPage />
        </ProtectedRoute>
      } />
      <Route path="/app/tuition-calculator" element={
        <ProtectedRoute>
          <TuitionCalculatorPage />
        </ProtectedRoute>
      } />

      {/* NC Framework Routes — Wake / NC Plan and NC CDP now point to the same
          NC Career Plan surface. FuturePathCDPPage was the older duplicate. */}
      <Route path="/app/my-cdp" element={
        <ProtectedRoute>
          <NCCareerPlanPage />
        </ProtectedRoute>
      } />
      <Route path="/app/my-school-plan" element={
        <ProtectedRoute>
          <MySchoolPlanPage />
        </ProtectedRoute>
      } />
      <Route path="/app/nc-cdp" element={
        <ProtectedRoute>
          <NCCareerPlanPage />
        </ProtectedRoute>
      } />
      <Route path="/app/nc-cte" element={
        <ProtectedRoute>
          <NCCTEPathwaysPage />
        </ProtectedRoute>
      } />
      <Route path="/app/nc-wbl" element={
        <ProtectedRoute>
          <WBLTrackerPage />
        </ProtectedRoute>
      } />
      <Route path="/app/certificates" element={
        <ProtectedRoute>
          <CertificateProgramsPage />
        </ProtectedRoute>
      } />

      {/* New Feature Pages */}
      <Route path="/app/credentials" element={
        <ProtectedRoute>
          <CredentialTrackerPage />
        </ProtectedRoute>
      } />
      <Route path="/app/graduation-readiness" element={
        <ProtectedRoute>
          <GraduationPredictionPage />
        </ProtectedRoute>
      } />
      <Route path="/app/course-planner" element={
        <ProtectedRoute>
          <CoursePlannerPage />
        </ProtectedRoute>
      } />
      <Route path="/app/academic-alignment" element={
        <ProtectedRoute>
          <AcademicAlignmentPage />
        </ProtectedRoute>
      } />
      <Route path="/app/events" element={
        <ProtectedRoute>
          <EventSchedulerPage />
        </ProtectedRoute>
      } />
      <Route path="/app/rec-letters" element={
        <ProtectedRoute>
          <RecommendationLettersPage />
        </ProtectedRoute>
      } />
      <Route path="/app/institutional-student" element={
        <ProtectedRoute>
          <InstitutionalStudentDashboard />
        </ProtectedRoute>
      } />

      {/* Public demo portals — no auth required */}
      <Route path="/demo/wake-county" element={<WakeCountyDemoPage />} />

      {/* Enterprise Routes */}
      <Route path="/enterprise" element={
        <ProtectedRoute>
          <EnterpriseDashboard />
        </ProtectedRoute>
      } />
      <Route path="/intelligence" element={
        <ProtectedRoute>
          <IntelligencePage />
        </ProtectedRoute>
      } />
      <Route path="/enterprise/super-admin" element={
        <ProtectedRoute>
          <EnterpriseDashboard />
        </ProtectedRoute>
      } />
      <Route path="/enterprise/cohorts" element={
        <ProtectedRoute>
          <EnterpriseDashboard />
        </ProtectedRoute>
      } />
      <Route path="/enterprise/roster" element={
        <ProtectedRoute>
          <EnterpriseDashboard />
        </ProtectedRoute>
      } />
      <Route path="/enterprise/organizations" element={
        <ProtectedRoute>
          <EnterpriseDashboard />
        </ProtectedRoute>
      } />
      
      {/* Admin Dashboard */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/tenants" element={
        <ProtectedRoute>
          <MasterAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <UserManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/consent-audit" element={
        <ProtectedRoute>
          <AdminConsentAuditPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/privacy-compliance" element={
        <ProtectedRoute>
          <AdminPrivacyCompliancePage />
        </ProtectedRoute>
      } />

      <Route path="/admin/exam-dates" element={
        <ProtectedRoute>
          <AdminExamDatesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/deployment-status" element={
        <ProtectedRoute>
          <AdminDeploymentStatusPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/qa-console" element={
        <ProtectedRoute>
          <AdminQAConsolePage />
        </ProtectedRoute>
      } />
      <Route path="/app/skills-assessment" element={
        <ProtectedRoute>
          <SkillsAssessmentPage />
        </ProtectedRoute>
      } />
      
      {/* Support Admin */}
      <Route path="/enterprise/support" element={
        <ProtectedRoute>
          <SupportAdminPage />
        </ProtectedRoute>
      } />
      
      {/* Employer/Workforce Intelligence Routes */}
      <Route path="/app/employer" element={
        <ProtectedRoute>
          <EmployerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/app/workforce" element={
        <ProtectedRoute>
          <EmployerDashboard />
        </ProtectedRoute>
      } />
      
      {/* Goals & Career Path Routes */}
      <Route path="/app/goals" element={
        <ProtectedRoute>
          <GoalsPage />
        </ProtectedRoute>
      } />
      <Route path="/app/paths/military" element={
        <ProtectedRoute>
          <MilitaryPathPage />
        </ProtectedRoute>
      } />
      <Route path="/app/paths/trades" element={
        <ProtectedRoute>
          <SkilledTradesPage />
        </ProtectedRoute>
      } />
      <Route path="/app/paths/workforce" element={
        <ProtectedRoute>
          <DirectWorkforcePage />
        </ProtectedRoute>
      } />
      <Route path="/app/future-careers" element={<Navigate to="/app/learn?tab=resources" replace />} />
      <Route path="/app/exam-dates" element={
        <ProtectedRoute>
          <ExamDatesPage />
        </ProtectedRoute>
      } />
      <Route path="/app/programs" element={
        <ProtectedRoute>
          <ProgramsPage />
        </ProtectedRoute>
      } />
      <Route path="/app/paths/entrepreneurship" element={
        <ProtectedRoute>
          <EntrepreneurshipPage />
        </ProtectedRoute>
      } />
      
      {/* Parent Routes */}
      <Route path="/parent/accept-invite" element={<ParentAcceptInvitePage />} />
      <Route path="/app/parent/child-cdp" element={
        <ProtectedRoute>
          <ParentCDPViewPage />
        </ProtectedRoute>
      } />
      <Route path="/parent/dashboard" element={
        <ProtectedRoute>
          <ParentDashboardPage />
        </ProtectedRoute>
      } />
      
      {/* Middle School Routes */}
      <Route path="/ms" element={
        <ProtectedRoute>
          <MiddleSchoolLayout />
        </ProtectedRoute>
      }>
        <Route path="home" element={<MiddleSchoolHome />} />
        <Route path="about-me" element={<MSAboutMePage />} />
        <Route path="quiz" element={<MSQuizPage />} />
        <Route path="strengths" element={<MSStrengthsPage />} />
        <Route path="careers" element={<MSCareersPage />} />
        <Route path="future-jobs" element={<MSFutureJobsPage />} />
        <Route path="dream-life" element={<MSDreamLifePage />} />
        <Route path="dreams" element={<MSDreamsPage />} />
        <Route path="missions" element={<MSMissionsPage />} />
        <Route path="programs" element={<MSProgramsPage />} />
        <Route path="learn" element={<MSLearnPage />} />
        <Route path="badges" element={<MSBadgesPage />} />
        <Route path="profile" element={<MSProfilePage />} />
        {/* Redirect /ms to /ms/home */}
        <Route index element={<Navigate to="/ms/home" replace />} />
      </Route>
      
      {/* Organization admin activation + the branded tenant portal at /<slug> (last, so every
          known route wins first) */}
      <Route path="/org/activate/:token" element={<OrgActivatePage />} />
      {/* A 13–17 young person taking ownership of the profile a guardian started */}
      <Route path="/family/accept/:token" element={<ClaimProfilePage />} />
      <Route path="/app/set-password" element={
        <ProtectedRoute><SetOwnPasswordPage /></ProtectedRoute>
      } />
      <Route path="/:slug" element={<OrgPortalPage />} />
      <Route path="/:slug/join" element={<TenantAuthPage mode="join" />} />
      <Route path="/:slug/signin" element={<TenantAuthPage mode="signin" />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Hide external badges using MutationObserver
  React.useEffect(() => {
    const hideExternalBadges = () => {
      // Find and hide any external badges
      const selectors = [
        '[class*="emergent"]',
        '[class*="Emergent"]',
        '[id*="emergent"]',
        '[data-testid*="emergent"]',
        'a[href*="emergentagent"]'
      ];
      
      selectors.forEach(selector => {
        try {
          document.querySelectorAll(selector).forEach(el => {
            if (el && el.style) {
              el.style.display = 'none';
              el.style.visibility = 'hidden';
              el.style.opacity = '0';
              el.style.pointerEvents = 'none';
            }
          });
        } catch (e) {}
      });
    };
    
    // Initial hide
    hideExternalBadges();
    
    // Watch for dynamic additions
    const observer = new MutationObserver((mutations) => {
      hideExternalBadges();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    return () => observer.disconnect();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <TenantProvider>
          <LearningStageProvider>
            <FocusModeProvider>
              <ExperienceModeProvider>
              <ActiveChildProvider>
                <PathScopeProvider>
                <FuturePathProvider>
                <CopyProvider>
                <CreditGate />
                <Toaster 
                  position="top-right" 
                  toastOptions={{
                    style: {
                      background: '#1e293b',
                      color: '#f8fafc',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }
                  }}
                />
                <TenantBrandProvider>
                  <AppRouter />
                  <SubjectExitPill />
                </TenantBrandProvider>
                <AdminVersionBanner />
                <DemoSwitcher />
                <DemoProfileSwitcher />
                </CopyProvider>
                </FuturePathProvider>
                </PathScopeProvider>
              </ActiveChildProvider>
              </ExperienceModeProvider>
            </FocusModeProvider>
          </LearningStageProvider>
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
